import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Dialog,
  Select,
  Button,
  Divider,
  MenuItem,
  TextField,
  Typography,
  InputLabel,
  IconButton,
  DialogContent,
  DialogActions
} from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import { API } from '../../../../api';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';
import ObjectiveMentors from './ObjectiveMentors';
import RecruitNewMentor from './RecruitNewMentor';
import { navigateToChat } from '../NavigateToChat';
import CallBookingModal from '../CallBookingModal';
import { appColors } from '../../../../utils/theme';
import { useForm, Controller } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { PlusCircleIcon } from '../../../../utils/Icons';
import { queryKeys } from '../../../../queries/queryKeys';
import { getUserDetails } from '../../../../utils/orgName';
import { useUserRole } from '../../../../utils/useUserRole';
import ChatAvatarComponent from '../chat/ChatAvatarComponent';
import useLearningObjs from '../../../../queries/useLearningObjs';
import { lockIcon, getStatusColor } from '../../components/common';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import CommonModalHeader from '../../../../components/DialogUi/CommonModalHeader';
import { generateDummyLeaderboardData } from '../../../../utils/dummyLoadingData';
import useProfileDetails from '../../../../hooks/useProfileDetails';

interface ObjectiveModalProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  onSubmit?: (data: any) => Promise<void>;
  description?: string;
  status: string;
  objId: string;
  notes?: Array<{
    note: string;
    createdAt: string;
  }>;
  mentorsList: any;
  allObjectives?: any;
  onSave?: (data: any) => void;
  myMenteesList?: any;
  isNewObjective?: boolean;
  matchedObjectives?: any[];
}

// const statusOptions = [
//   { value: 'to-do', label: 'To Do' },
//   { value: 'inprogress', label: 'In Progress' },
//   { value: 'completed', label: 'Completed' }
// ];

const ObjectiveModal: React.FC<ObjectiveModalProps> = ({
  open,
  onClose,
  onSubmit,
  title = '',
  description = '',
  status,
  objId,
  notes = [],
  mentorsList = [],
  allObjectives = [],
  onSave,
  myMenteesList,
  isNewObjective = false,
  matchedObjectives = []
}) => {
  const selectedGroupId = useAppSelector((state) => state.selectedGroup.data);
  const dispatch = useAppDispatch();
  const { isMentor, isMentee } = useUserRole();
  const [submitLoader, setSubmitLoader] = useState(false);
  const [recruitModalOpen, setRecruitModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const handleRecruitModalOpen = () => setRecruitModalOpen(true);
  const handleRecruitModalClose = () => setRecruitModalOpen(false);
  const { control } = useForm({
    defaultValues: {
      objectiveTitle: '',
      objectiveStatus: 'todo', // Default value maps to 'To Do'
      objectiveDescription: ''
    }
  });

  const [objDescription, setObjDescription] = useState(description);
  const [localMentorsList, setLocalMentorsList] = useState(mentorsList);
  const [localNotes, setLocalNotes] = useState(notes);
  const [mentor, setMentor] = useState(mentorsList);
  const { userId, personalDetails } = useProfileDetails();
  const [objTitle, setObjTitle] = useState(title);
  const [objStatus, setObjStatus] = useState(status);
  const [isDescriptionVisible, setDescriptionVisible] = useState(false);
  const [note, setNote] = useState(''); // To manage the note text
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [displayName, setDisplayName] = useState('');
  const [participantUser, setParticipantUser] = useState<any>([]);
  const [objectiveTitleOptions, setObjectiveTitleOptions] = useState<{ id: string; value: string; label: string }[]>(
    []
  );
  const [objectiveSelected, setObjectiveSelected] = useState(!isNewObjective);
  const [selectedObjective, setSelectedObjective] = useState<{ id: string; title: string }>({ id: objId, title });
  const [currentObjectiveIndex, setCurrentObjectiveIndex] = useState(0);
  const [shouldResetIndex, setShouldResetIndex] = useState(false);

  const navigateMember = (member: any) => {
    setSelectedMember(member);
  };

  const [bookedCallDetails, setBookedCallDetails] = useState<any>('');
  const [openCalendarModal, setOpenCalendarModal] = useState(false);
  const handleCalendarOpen = (each: any) => {
    localStorage.setItem('isRefetchMentors', 'true');
    setDisplayName(each?.name);
    setParticipantUser([each]);
    setBookedCallDetails('');
    setOpenCalendarModal(true);
  };

  const handleCalendarClose = () => {
    setDisplayName('');
    setParticipantUser([]);
    setBookedCallDetails('');
    setOpenCalendarModal(false);
  };

  const communityMembers = useAppSelector((state) => state.acsCommunityUsers.data);
  const personalThreadsArr = useAppSelector((state) => state.acsChannels.personalThreadsArr);
  const navigate = useNavigate();
  const { orgId } = getUserDetails();
  const grpId = selectedGroupId?.id;
  const queryClient = useQueryClient();
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setObjDescription(description);
      setObjTitle(title);
      setObjStatus(status);
      setNote('');
      if (isMentor && objId) {
        const storedNotes = localStorage.getItem(`mentor_notes_${objId}`);
        if (storedNotes) {
          setLocalNotes(JSON.parse(storedNotes));
        } else {
          setLocalNotes(notes);
        }
      } else {
        setLocalNotes(notes);
      }

      if (shouldResetIndex) {
        setCurrentObjectiveIndex(0);
        setShouldResetIndex(false);
      }

      if (isMentor) {
        const currentMentor = mentorsList?.filter((m: any) => m.userId === userId) || [];
        setMentor(currentMentor);
      } else {
        setLocalMentorsList(mentorsList);
      }
      setObjectiveSelected(!isNewObjective);
      setSelectedObjective({ id: objId, title });
      setIsLoading(false);
    }
  }, [open, description, title, status, notes, mentorsList, isNewObjective, objId, userId, isMentor, shouldResetIndex]);
  const { data: objectives, refetch } = useLearningObjs({ orgId, grpId });
  const learningObjectives = objectives?.data?.data;

  const udpdateObjQuery = async () => {
    queryClient.setQueryData([queryKeys.GET_LEARNING_OBJS, grpId], (oldData: any) => {
      const newData = oldData.data.data.map((objective: any) => {
        if (objective.id === objId) {
          const newNotes =
            note?.trim() !== '' ? [...objective.notes, { note, createdAt: Math.floor(Date.now() / 1000) }] : notes;
          return {
            ...objective,
            objective: objTitle,
            description: objDescription,
            status: objStatus,
            notes: newNotes,
            mentors: localMentorsList
          };
        }
        return objective;
      });
      return {
        ...oldData,
        data: {
          data: newData
        }
      };
    });
  };

  const updateQueryWithNewMeeting = (meetingDetails: any) => {
    queryClient.setQueryData([queryKeys.GET_LEARNING_OBJS, grpId], (oldData: any) => {
      if (!oldData) return oldData;
      const newData = oldData.data.data.map((objective: any) => {
        if (objective.id === objId) {
          const mentorId = participantUser[0]?.id;

          const updatedMentors = objective.mentors.map((mentor: any) => {
            if (mentor.id === mentorId) {
              return {
                ...mentor,
                meetings: [...(mentor.meetings || []), meetingDetails]
              };
            }
            return mentor;
          });

          return {
            ...objective,
            mentors: updatedMentors
          };
        }
        return objective;
      });

      return {
        ...oldData,
        data: {
          data: newData
        }
      };
    });
  };

  const onMeetingScheduled = (meetingDetails: any) => {
    updateQueryWithNewMeeting(meetingDetails);
    setLocalMentorsList((current: any) => {
      return current.map((mentor: any) => {
        if (mentor.id === participantUser[0]?.id) {
          return {
            ...mentor,
            meetings: [...(mentor.meetings || []), meetingDetails]
          };
        }
        return mentor;
      });
    });
  };

  // Handler to toggle the visibility of the description box
  const handleAddNewNoteClick = () => {
    setDescriptionVisible(true);
  };

  // Handler to update the note
  const handleNoteChange = (event: any) => {
    setNote(event.target.value);
  };

  // Handler to close the description box and display the note
  const handleCloseDescriptionBox = () => {
    setDescriptionVisible(false);
  };

  const handleObjectiveSelection = (e: any) => {
    const value = e.target.value;
    const selected = objectiveTitleOptions.find((opt) => opt.value === value);

    if (selected) {
      setSelectedObjective({
        id: selected.id,
        title: selected.value
      });
      setObjTitle(selected.value);
      setObjectiveSelected(true);
    } else {
      setSelectedObjective({
        id: '',
        title: value
      });
      setObjTitle(value);
      setObjectiveSelected(false);
    }
  };

  const updateObjective = async () => {
    const newNotes = note?.trim() !== '' ? [...notes, { note, createdAt: Math.floor(Date.now() / 1000) }] : notes;
    const data = {
      id: objId,
      objective: objTitle,
      description: objDescription,
      status: objStatus,
      notes: newNotes
    };
    const response = await API.updateMenteeLearningObjectives({ objId, data });
    if (response.status === 200) {
      refetch();
    }
  };

  const handleSubmit = async () => {
    setSubmitLoader(true);
    try {
      const newNotes = note?.trim() !== '' ? [...notes, { note, createdAt: Math.floor(Date.now() / 1000) }] : notes;
      //mentor save logic
      if (isMentor) {
        if (note?.trim() !== '') {
          const menteeData = myMenteesList;
          if (menteeData && Array.isArray(menteeData.matchedObjectives)) {
            const matchedObjective = menteeData.matchedObjectives[currentObjectiveIndex];
            if (matchedObjective) {
              const requestBody = {
                menteeId: menteeData.menteeId,
                notes: note
              };

              const response = await API.updateObjectiveNotes({
                objId: matchedObjective.id,
                data: requestBody
              });

              if (response.status === 200) {
                const updatedNotes = [
                  ...localNotes,
                  {
                    note: note,
                    createdAt: Math.floor(Date.now() / 1000).toString()
                  }
                ];

                localStorage.setItem(`mentor_notes_${matchedObjective.id}`, JSON.stringify(updatedNotes));

                queryClient.invalidateQueries({
                  queryKey: [queryKeys.GET_LEARNING_OBJS, grpId]
                });
                await refetch();
                setCurrentObjectiveIndex(0);
              }
            }
          }
        }
        onClose();
        return;
      }
      // Mentee save logic
      const objectiveData = {
        id: selectedObjective.id,
        objective: objTitle,
        description: objDescription,
        status: objStatus,
        notes: newNotes,
        mentors: localMentorsList
      };

      if (isNewObjective) {
        await API.saveLearningObjectives({
          learningObjectives: [objectiveData]
        });

        await queryClient.invalidateQueries({
          queryKey: [queryKeys.GET_LEARNING_OBJS, grpId]
        });
        await refetch();
      } else {
        const updatedData = {
          ...objectiveData,
          id: objId
        };

        onSave && onSave(updatedData);
        await updateObjective();
        await udpdateObjQuery();
      }
      onClose();
    } catch (error) {
      console.error('Error saving objective:', error);
    } finally {
      setSubmitLoader(false);
    }
  };
  useEffect(() => {
    const fetchObjectiveOptions = async () => {
      try {
        const { orgId } = getUserDetails();
        const response = await API.getUserLearningObjectives({ orgId });
        const allApiObjectives = response.data?.data || [];
        const usedObjectives = learningObjectives
          .filter((obj: any) => obj.objective?.trim())
          .map((obj: any) => obj.objective);

        const availableOptions = allApiObjectives
          .filter((objective: any) => !usedObjectives.includes(objective.objective))
          .map((objective: any) => ({
            id: objective.id,
            value: objective.objective,
            label: objective.objective
          }));
        setObjectiveTitleOptions(availableOptions);
      } catch (error) {
        console.error('Error fetching objective options:', error);
      }
    };

    if (isNewObjective) {
      fetchObjectiveOptions();
    }
  }, [isNewObjective, learningObjectives]);

  const handleNavigateToChat = () => {
    let communicationId = _.get(selectedMember, 'communicationId', '');
    const listedInCommunity = communityMembers?.find((each) => each?.id?.communicationId === communicationId);
    let isInCommunity = false;
    if (listedInCommunity) {
      isInCommunity = true;
    }
    navigateToChat(communicationId, isInCommunity, personalThreadsArr, navigate, dispatch);
  };

  useEffect(() => {
    if (selectedMember) {
      handleNavigateToChat();
    }
  }, [selectedMember]);

  /** Previous arrow click */
  const handlePrevClick = () => {
    if (isMentor) {
      if (currentObjectiveIndex > 0) {
        const prevIndex = currentObjectiveIndex - 1;
        const prevObjective = matchedObjectives[prevIndex];
        setCurrentObjectiveIndex(prevIndex);
        setObjDescription(prevObjective.description || '');
        setObjTitle(prevObjective.objective || '');
        setObjStatus(prevObjective.status || 'to-do');
        const currentMentor = prevObjective.mentors?.filter((m: any) => m.userId === userId) || [];
        setMentor(currentMentor);
        setLocalMentorsList(prevObjective.mentors || []);
        setSelectedObjective({ id: prevObjective.id, title: prevObjective.objective });
        setLocalNotes(prevObjective.notes || []);
        setNote('');

        onSubmit &&
          onSubmit({
            action: 'navigate',
            direction: 'prev',
            objectiveId: prevObjective.id
          });
      }
    } else {
      const currentIndex = allObjectives.findIndex((obj: any) => obj.id === objId);
      if (currentIndex > 0) {
        const prevObjective = allObjectives[currentIndex - 1];
        onSubmit &&
          onSubmit({
            action: 'navigate',
            direction: 'prev',
            objectiveId: prevObjective.id
          });
      }
    }
    setShouldResetIndex(false);
  };

  /** Next arrow click  */
  const handleNextClick = () => {
    if (isMentor) {
      if (currentObjectiveIndex < matchedObjectives.length - 1) {
        const nextIndex = currentObjectiveIndex + 1;
        const nextObjective = matchedObjectives[nextIndex];
        setCurrentObjectiveIndex(nextIndex);
        setObjDescription(nextObjective.description || '');
        setObjTitle(nextObjective.objective || '');
        setObjStatus(nextObjective.status || 'to-do');
        const currentMentor = nextObjective.mentors?.filter((m: any) => m.userId === userId) || [];
        setMentor(currentMentor);
        setNote('');
        setLocalMentorsList(nextObjective.mentors || []);
        setSelectedObjective({ id: nextObjective.id, title: nextObjective.objective });
        setLocalNotes(nextObjective.notes || []);

        onSubmit &&
          onSubmit({
            action: 'navigate',
            direction: 'next',
            objectiveId: nextObjective.id
          });
      }
    } else {
      const currentIndex = allObjectives.findIndex((obj: any) => obj.id === objId);
      if (currentIndex < allObjectives.length - 1) {
        const nextObjective = allObjectives[currentIndex + 1];
        onSubmit &&
          onSubmit({
            action: 'navigate',
            direction: 'next',
            objectiveId: nextObjective.id
          });
      }
    }
    setShouldResetIndex(false);
  };
  const handleClose = () => {
    if (!submitLoader) {
      setCurrentObjectiveIndex(0);
    }
    onClose();
  };
  return (
    <>
      <Dialog open={open} fullWidth onClose={handleClose} PaperProps={{ sx: { height: '100%' } }}>
        {/* Modal Header */}
        <CommonModalHeader
          title={objTitle || 'Add Objective'}
          handleCloseModal={handleClose}
          nextPrev={
            title
              ? {
                  onPrevClick: handlePrevClick,
                  onNextClick: handleNextClick,
                  isPrevDisabled: isMentor
                    ? currentObjectiveIndex <= 0
                    : allObjectives.findIndex((obj: any) => obj.id === objId) <= 0,
                  isNextDisabled: isMentor
                    ? currentObjectiveIndex >= matchedObjectives.length - 1
                    : allObjectives.findIndex((obj: any) => obj.id === objId) >= allObjectives.length - 1
                }
              : undefined
          }
        />
        {/* Modal Content */}
        <DialogContent className="p-0">
          <Grid container spacing={2} p={2} pb={isMentor ? 0 : 2}>
            {/* Objective Title */}
            {!title && isNewObjective && (
              <Grid item xs={12}>
                <Box display={'flex'} gap={1}>
                  <InputLabel htmlFor="objective-title">Objective Title {isMentor && lockIcon({})}</InputLabel>
                </Box>
                <Controller
                  name="objectiveTitle"
                  control={control}
                  render={({ field }) => (
                    <Select
                      fullWidth
                      {...field}
                      id="objective-title"
                      variant="outlined"
                      displayEmpty
                      placeholder="Enter Objective Title"
                      value={objTitle}
                      onChange={handleObjectiveSelection}
                      renderValue={(selected) => selected || 'Select Objective'}
                    >
                      {objectiveTitleOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid>
            )}
            {/*  ) : (
               <Grid item xs={12}>
                <Box display={'flex'} gap={1}>
                   <InputLabel htmlFor="objective-title">Objective Title {isMentor && lockIcon({})}</InputLabel>
                 </Box>
                 <TextField fullWidth id="objective-title" variant="outlined" value={objTitle} disabled />
               </Grid>
             )} */}

            {/* For Mentor */}
            {isMentor && (
              <Box textAlign={'center'} width={'100%'} mt={2}>
                <Typography variant="subtitle2" color={appColors.blue1}>
                  You have been matched to help with this objective
                </Typography>
              </Box>
            )}

            {/* Objective Description */}
            {/* <Grid item xs={12}>
              <Box display={'flex'} gap={0.5}>
                <InputLabel htmlFor="objective-description">
                  Objective Description {isMentor && lockIcon({})}
                </InputLabel>
              </Box>
              <Controller
                name="objectiveDescription"
                control={control}
                render={({ field }) => (
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    {...field}
                    variant="outlined"
                    disabled={isMentor}
                    id="objective-description"
                    placeholder="Outline for mentors how this objective is relevant to your needs specifically"
                    value={objDescription}
                    onChange={(e) => setObjDescription(e.target.value)}
                  />
                )}
              />
            </Grid> */}

            {/* Objective Status */}
            {/* <Grid item xs={12} className={isMentor ? 'pointerEvents-none' : ''}>
              <Box display={'flex'}>
                <InputLabel htmlFor="objective-status">Objective Status {isMentor && lockIcon({})}</InputLabel>
              </Box>
              <Controller
                name="objectiveStatus"
                control={control}
                render={({ field }) => (
                  <Select
                    fullWidth
                    {...field}
                    id="objective-status"
                    sx={{
                      backgroundColor: getStatusColor(objStatus),
                      color: '#FFFFFF',
                      '& .MuiSelect-icon': {
                        color: '#FFFFFF', // Change the icon color to white
                        display: isMentor ? 'none' : 'unset' // Hide icon if isMentor is true
                      }
                    }}
                    value={objStatus}
                    onChange={(e) => {
                      console.log('e.target.value', e.target.value);
                      setObjStatus(e.target.value);
                    }}
                    disabled={isLoading} 
                  >
                    {statusOptions?.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Grid> */}

            {/* Objective Mentors */}
            <Grid item xs={12}>
              {/* {isMentee && <Typography>Objective Mentors</Typography>} */}
              {isMentee && (
                <Box textAlign={'center'}>
                  <Typography variant="h5" mb={1}>
                    My Mentors
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleRecruitModalOpen}
                    disabled={isNewObjective && !objectiveSelected}
                  >
                    <PlusCircleIcon color="primary" size={30} />
                  </IconButton>
                </Box>
              )}

              {/* If no mentors, show placeholder text */}
              {/* {mentorsList?.length === 0 ? (
                <Typography textAlign={'center'} mb={2}>
                  No mentors available
                </Typography>
              ) : ( */}
              <ObjectiveMentors
                mentorsList={isMentor ? mentor : mentorsList}
                onChatClick={(each) => navigateMember(each)}
                onCalendarClick={(each) => handleCalendarOpen(each)}
                myMenteesList={myMenteesList}
                objTitle={objTitle}
              />
              {/* )} */}

              {/* Divider added */}
              {/* {mentorsList?.length !== 0 && <Divider className="mb-20" sx={{ borderColor: "#c6c7cb", opacity: 1 }} />} */}

              {/* {isMentee && (
                <Grid container justifyContent="center" alignItems="center" mt={mentorsList?.length === 0 ? 1 : 0}>
                  <Button
                    variant="contained"
                    className="w-sm-50p fs-14"
                    disabled={isNewObjective && !objectiveSelected}
                    onClick={handleRecruitModalOpen}
                  >
                    Find new Mentor
                  </Button>
                </Grid>
              )} */}
            </Grid>
          </Grid>
          <Divider />

          {/* Objective Notes */}
          <Grid container spacing={2} p={2}>
            <Grid item xs={12}>
              <Typography variant="h5" textAlign={'center'}>
                Your Feedback
              </Typography>
              {/* If description box is visible, show TextField for entering the note */}
              {isDescriptionVisible ? (
                <Box mt={2}>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    variant="outlined"
                    placeholder="Enter your feedback"
                    value={note}
                    onChange={handleNoteChange}
                    onBlur={handleCloseDescriptionBox}
                  />
                </Box>
              ) : localNotes?.length > 0 ? (
                localNotes[0]?.note ? (
                  localNotes.map((noteItem: any, index: number) => (
                    <React.Fragment key={index}>
                      <Box display={'flex'} alignItems={'flex-start'} className="mt-10 gap-20 w-100p">
                        <ChatAvatarComponent
                          image={personalDetails?.headshot || ''}
                          type="noStatus"
                          firstLetter={personalDetails?.name?.charAt(0) || 'M'}
                          width={'48px'}
                          height={'48px'}
                          cursor="pointer"
                          loading={submitLoader}
                        />
                        <Box display={'flex'} flexDirection={'column'} flex={1} overflow={'hidden'}>
                          <Typography variant="h5" flex={1} noWrap>
                            {personalDetails?.name || 'You'}
                          </Typography>
                          <Typography variant="body1">
                            {moment.unix(parseInt(noteItem.createdAt)).format('MM/DD/YYYY')}
                          </Typography>
                          <Typography className="mt-10" variant="body1">
                            {noteItem.note}
                          </Typography>
                        </Box>
                      </Box>
                      {index < localNotes.length - 1 && <Divider className="my-20" />}
                    </React.Fragment>
                  ))
                ) : (
                  localNotes.map((feedback: any, index: number) => (
                    <React.Fragment key={index}>
                      {feedback?.notes?.map((noteItem: any, noteIndex: number) => (
                        <React.Fragment key={noteIndex}>
                          <Box display={'flex'} alignItems={'flex-start'} className="mt-10 gap-20 w-100p">
                            <ChatAvatarComponent
                              image={feedback.headshot}
                              type="noStatus"
                              firstLetter={feedback.name.charAt(0)}
                              width={'48px'}
                              height={'48px'}
                              cursor="pointer"
                              loading={submitLoader}
                            />
                            <Box display={'flex'} flexDirection={'column'} flex={1} overflow={'hidden'}>
                              <Typography variant="h5" flex={1} noWrap>
                                {feedback.name}
                              </Typography>
                              <Typography variant="body1">
                                {moment.unix(parseInt(noteItem.createdAt)).format('MM/DD/YYYY')}
                              </Typography>
                              <Typography className="mt-10" variant="body1" flex={1} noWrap>
                                {noteItem.note}
                              </Typography>
                            </Box>
                          </Box>
                          {noteIndex < feedback.notes.length - 1 && <Divider className="my-20" />}
                        </React.Fragment>
                      ))}
                      {index < localNotes.length - 1 && <Divider className="my-20" />}
                    </React.Fragment>
                  ))
                )
              ) : (
                <Box display={'flex'} justifyContent={'center'} alignItems={'center'} mt={2} width={'100%'}>
                  <Typography>No feedback added yet</Typography>
                </Box>
              )}

              {!isDescriptionVisible && isMentor && (
                <Grid container justifyContent="center" alignItems="center">
                  <Button
                    color="inherit"
                    variant="contained"
                    className="w-50p fs-14 mt-20 hover-inherit"
                    onClick={handleAddNewNoteClick}
                  >
                    Add New Feedback
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        {/* Modal Actions */}
        {(isMentee || isMentor) && (
          <DialogActions>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Button onClick={onClose} variant="outlined" fullWidth>
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <LoadingButton onClick={handleSubmit} variant="contained" loading={submitLoader} fullWidth>
                  Save
                </LoadingButton>
              </Grid>
            </Grid>
          </DialogActions>
        )}
      </Dialog>

      {/* Find new Mentor Modal */}
      {recruitModalOpen && (
        <RecruitNewMentor
          title={objTitle}
          open={recruitModalOpen}
          onClose={handleRecruitModalClose}
          mentorsList={mentorsList}
          onProfileClick={(mid) => console.log(`Profile clicked: ${mid}`)}
          onChatClick={(each) => navigateMember(each)}
          onCalendarClick={(each) => handleCalendarOpen(each)}
          objId={objId || selectedObjective.id}
          grpId={grpId}
          apiType="objective"
        />
      )}

      {/* Call schedule modal  */}
      <CallBookingModal
        title={title}
        handleCloseModal={handleCalendarClose}
        openFlag={openCalendarModal}
        chatProfileName={displayName}
        threadParticipantsUsers={participantUser}
        chatType="Chats"
        pageName="Mentor"
        bookedCallDetails={bookedCallDetails}
        onMeetingScheduled={onMeetingScheduled}
      />
    </>
  );
};

export default ObjectiveModal;
